import { HOME_PAGE_INTERVAL_FILTER_VALUES } from '@/utils/homepage'

export const AI_REPORT_TYPES = {
  ALIGNMENT: 1,
  ENGAGEMENT: 2
}

export const AI_REPORT_GENERATION_STATUSES = {
  NONE: 'NONE',
  GENERATING: 'GENERATING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  TIMED_OUT: 'TIMED_OUT'
}

export const AI_REPORT_PERIODS = {
  LAST_14_DAYS: 'LAST_14_DAYS',
  LAST_30_DAYS: 'LAST_30_DAYS',
  LAST_90_DAYS: 'LAST_90_DAYS',
  LAST_WEEK: 'LAST_WEEK',
  LAST_MONTH: 'LAST_MONTH',
  LAST_QUARTER: 'LAST_QUARTER'
}

export const DEFAULT_AI_REPORT_PERIOD = AI_REPORT_PERIODS.LAST_14_DAYS

// the names of the Interval filter options as the Home filter shows them (FR-0.27)
export const AI_REPORT_INTERVAL_FILTER_LABEL_KEYS = {
  [HOME_PAGE_INTERVAL_FILTER_VALUES.ALL_CURRENT]: 'homepage.interval_filter.all_current',
  [HOME_PAGE_INTERVAL_FILTER_VALUES.QUARTER]: 'homepage.interval_filter.current_quarters',
  [HOME_PAGE_INTERVAL_FILTER_VALUES.YEAR]: 'homepage.interval_filter.current_years'
}

export const AI_REPORT_COMMENTARY_CODES = {
  ZERO_ENGAGEMENT: 'ZERO_ENGAGEMENT',
  HIGH_ENGAGEMENT: 'HIGH_ENGAGEMENT',
  CHECKED_IN_FOR_OTHERS_ONLY: 'CHECKED_IN_FOR_OTHERS_ONLY',
  NO_CHECKINS_PEOPLE: 'NO_CHECKINS_PEOPLE',
  NO_CHECKINS_AT_ALL: 'NO_CHECKINS_AT_ALL'
}

// keys of GET /homepage/aiReport and POST /homepage/aiReport/generate
export const AI_REPORT_RESPONSE_KEYS = {
  SELECTION: 'selection',
  ALIGNMENT: 'alignment',
  ENGAGEMENT: 'engagement',
  CAN_GENERATE: 'canGenerate',
  GENERATION_STATUS: 'generationStatus',
  REPORT: 'report',
  REASON: 'reason'
}

export const AI_REPORT_KEYS = {
  ID: 'id',
  GENERATED_AT: 'generatedAt',
  APPLIED_FILTERS: 'appliedFilters',
  OVERALL_SCORE_PERCENT: 'overallScorePercent',
  ELEMENTS: 'elements',
  PAIRS: 'pairs',
  SECTIONS: 'sections',
  SUMMARY: 'summary',
  RISKS: 'risks',
  PERIOD: 'period',
  ENGAGEMENT_PERCENT: 'engagementPercent',
  ROWS: 'rows',
  COMMENTARY: 'commentary'
}

export const AI_REPORT_PERIOD_KEYS = {
  TYPE: 'type',
  NAME: 'name',
  START: 'start',
  END: 'end'
}

export const AI_REPORT_ELEMENT_KEYS = {
  ELEMENT_ID: 'elementId',
  DISPLAY_ID: 'displayId',
  NAME: 'name',
  TYPE: 'type',
  LEVEL: 'level',
  PARENT_ELEMENT: 'parentElement',
  CRITERIA: 'criteria',
  SCORE: 'score',
  MAX_SCORE: 'maxScore',
  MASKED: 'masked',
  DELETED: 'deleted'
}

export const AI_REPORT_ELEMENT_TYPES = {
  OBJECTIVE: 'OBJECTIVE',
  KR: 'KR'
}

// the Element type glyphs of the design: a target for an Objective, a bar chart for a Metric
export const AI_REPORT_ELEMENT_TYPE_ICONS = {
  [AI_REPORT_ELEMENT_TYPES.OBJECTIVE]: 'target',
  [AI_REPORT_ELEMENT_TYPES.KR]: 'bar-chart'
}

// the four criteria of the agreed prompt, in the priority order of the recommendations (FR-1.19)
export const AI_REPORT_CRITERIA = {
  LINK: 'link',
  MEASURABILITY: 'measurability',
  RESULT: 'result',
  LEVEL: 'level'
}

export const AI_REPORT_CRITERION_LABEL_KEYS = {
  [AI_REPORT_CRITERIA.LINK]: 'homepage.ai_reports.alignment.criterion.link',
  [AI_REPORT_CRITERIA.MEASURABILITY]: 'homepage.ai_reports.alignment.criterion.measurability',
  [AI_REPORT_CRITERIA.RESULT]: 'homepage.ai_reports.alignment.criterion.result',
  [AI_REPORT_CRITERIA.LEVEL]: 'homepage.ai_reports.alignment.criterion.level'
}

// a row without a parent is a pair with a null PARENT_ID and the Element as CHILD_ID (FR-1.15)
export const AI_REPORT_PAIR_KEYS = {
  PAIR_ID: 'pairId',
  PARENT_ID: 'parentId',
  CHILD_ID: 'childId',
  CHILD_SCORE: 'childScore',
  CHILD_MAX_SCORE: 'childMaxScore',
  ALIGNMENT_SCORE: 'alignmentScore',
  ALIGNMENT_MAX_SCORE: 'alignmentMaxScore',
  LINK_TYPE: 'linkType',
  PATTERN: 'pattern',
  LINK_SCORE: 'linkScore',
  RECOMMENDATIONS: 'recommendations',
  // the one or two recommendations the LLM picks per pair are kept and not shown (D-33)
  PICKED_RECOMMENDATIONS: 'pickedRecommendations'
}

export const AI_REPORT_RECOMMENDATION_KEYS = {
  ELEMENT_ID: 'elementId',
  CRITERION: 'criterion',
  TEXT: 'text'
}

// the text of a recommendation names Elements as {{ID}}, where ID is the Element reference
export const AI_REPORT_REFERENCE_PLACEHOLDER_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g

export const AI_REPORT_SECTION_KEYS = {
  GROUP: 'group',
  PAIR_IDS: 'pairIds'
}

export const AI_REPORT_SECTION_GROUP_KEYS = {
  GROUP_ID: 'groupId',
  GROUP_NAME: 'groupName'
}

// the risks are kept with the report and not shown (FR-1.10, D-27)
export const AI_REPORT_RISK_KEYS = {
  TYPE: 'type',
  ELEMENT_IDS: 'elementIds',
  TEXT: 'text'
}

export const AI_REPORT_ROW_KEYS = {
  OWNER: 'owner',
  CHECKED_IN: 'checkedIn',
  ELEMENTS_COUNT: 'elementsCount',
  CHECKINS_COUNT: 'checkinsCount',
  EXPECTED_CHECKINS: 'expectedCheckins',
  LAST_UPDATE: 'lastUpdate',
  RATE_PERCENT: 'ratePercent'
}

export const AI_REPORT_OWNER_KEYS = {
  ACCOUNT_ID: 'accountId',
  NAME: 'name'
}

export const AI_REPORT_COMMENTARY_KEYS = {
  CODE: 'code',
  PARAMS: 'params'
}

export const AI_REPORT_COMMENTARY_PARAM_KEYS = {
  NAME: 'name',
  NAMES: 'names',
  PERCENT: 'percent'
}

export const AI_REPORT_COMMENTARY_LABEL_KEYS = {
  [AI_REPORT_COMMENTARY_CODES.ZERO_ENGAGEMENT]:
    'homepage.ai_reports.engagement.commentary.zero_engagement',
  [AI_REPORT_COMMENTARY_CODES.HIGH_ENGAGEMENT]:
    'homepage.ai_reports.engagement.commentary.high_engagement',
  [AI_REPORT_COMMENTARY_CODES.CHECKED_IN_FOR_OTHERS_ONLY]:
    'homepage.ai_reports.engagement.commentary.checked_in_for_others_only',
  [AI_REPORT_COMMENTARY_CODES.NO_CHECKINS_PEOPLE]:
    'homepage.ai_reports.engagement.commentary.no_checkins_people',
  [AI_REPORT_COMMENTARY_CODES.NO_CHECKINS_AT_ALL]:
    'homepage.ai_reports.engagement.commentary.no_checkins_at_all'
}

// above this number of matching pairs the Alignment report shows only the weakest ones
export const AI_REPORT_PAIRS_LIMIT = 30

export const AI_REPORT_WEAKEST_PAIRS_COUNT = 20

export const AI_REPORT_EMPTY_VALUE = '—'

// the "Alignment" and "Engagement" figures of the header card without a report (screen 1.1.1)
export const AI_REPORT_FIGURE_EMPTY_VALUE = '– –'

// a score is coloured by its share of the maximum: green from HIGH, yellow from MEDIUM, red below;
// the design shows 7/10 green and 4/10 yellow
export const AI_REPORT_SCORE_GRADES = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low'
}

export const AI_REPORT_SCORE_GRADE_THRESHOLDS_PERCENT = {
  [AI_REPORT_SCORE_GRADES.HIGH]: 70,
  [AI_REPORT_SCORE_GRADES.MEDIUM]: 40
}

export const AI_REPORT_MASKED_ELEMENT_NAME = '***'

export const AI_REPORT_SECTION_KEY_BY_TYPE = {
  [AI_REPORT_TYPES.ALIGNMENT]: AI_REPORT_RESPONSE_KEYS.ALIGNMENT,
  [AI_REPORT_TYPES.ENGAGEMENT]: AI_REPORT_RESPONSE_KEYS.ENGAGEMENT
}

// the generate endpoint answers 422 when the Alignment input has no pair
export const AI_REPORT_NOTHING_TO_ANALYSE_STATUS = 422

export const AI_REPORT_POLLING_INTERVAL_MS = 3000

// keys of GET /homepage/aiReport/debug/settings
export const AI_REPORT_DEBUG_SETTINGS_RESPONSE_KEYS = {
  MODELS: 'models',
  EFFORTS: 'efforts',
  DEFAULTS: 'defaults',
  PROMPTS: 'prompts'
}

export const AI_REPORT_DEBUG_MODEL_KEYS = {
  ID: 'id',
  NAME: 'name',
  PRICED: 'priced'
}

export const AI_REPORT_DEBUG_PROMPT_KEYS = {
  SYSTEM: 'system',
  USER: 'user',
  SUMMARY_SYSTEM: 'summarySystem',
  SUMMARY_USER: 'summaryUser'
}

// keys of the debug object of POST /homepage/aiReport/generate; the served defaults use the
// keys of the model, the effort and the three numbers
export const AI_REPORT_DEBUG_KEYS = {
  MODEL: 'model',
  EFFORT: 'effort',
  SYSTEM_PROMPT: 'systemPrompt',
  USER_PROMPT: 'userPrompt',
  SUMMARY_SYSTEM_PROMPT: 'summarySystemPrompt',
  SUMMARY_USER_PROMPT: 'summaryUserPrompt',
  TIME_LIMIT_SECONDS: 'timeLimitSeconds',
  PAIRS_PER_CALL: 'pairsPerCall',
  ELEMENTS_PER_CALL: 'elementsPerCall'
}

export const AI_REPORT_DEBUG_REQUEST_KEY = 'debug'

// the prompt of each debug field as the settings serve it (FR-2.1, FR-2.2)
export const AI_REPORT_DEBUG_PROMPT_KEY_BY_FIELD = {
  [AI_REPORT_DEBUG_KEYS.SYSTEM_PROMPT]: AI_REPORT_DEBUG_PROMPT_KEYS.SYSTEM,
  [AI_REPORT_DEBUG_KEYS.USER_PROMPT]: AI_REPORT_DEBUG_PROMPT_KEYS.USER,
  [AI_REPORT_DEBUG_KEYS.SUMMARY_SYSTEM_PROMPT]: AI_REPORT_DEBUG_PROMPT_KEYS.SUMMARY_SYSTEM,
  [AI_REPORT_DEBUG_KEYS.SUMMARY_USER_PROMPT]: AI_REPORT_DEBUG_PROMPT_KEYS.SUMMARY_USER
}

export const AI_REPORT_DEBUG_NUMBER_FIELDS = [
  AI_REPORT_DEBUG_KEYS.TIME_LIMIT_SECONDS,
  AI_REPORT_DEBUG_KEYS.PAIRS_PER_CALL,
  AI_REPORT_DEBUG_KEYS.ELEMENTS_PER_CALL
]

// with "default" the call carries no effort and the model uses its own (FR-1.2)
export const AI_REPORT_DEBUG_EFFORTS = {
  LOW: 'low',
  MEDIUM: 'medium',
  DEFAULT: 'default'
}

export const AI_REPORT_DEBUG_EFFORT_LABEL_KEYS = {
  [AI_REPORT_DEBUG_EFFORTS.LOW]: 'homepage.ai_reports.debug.effort.low',
  [AI_REPORT_DEBUG_EFFORTS.MEDIUM]: 'homepage.ai_reports.debug.effort.medium',
  [AI_REPORT_DEBUG_EFFORTS.DEFAULT]: 'homepage.ai_reports.debug.effort.default'
}

// where Oboard inserts the input object of a call into a user prompt (C-2.2)
export const AI_REPORT_DEBUG_PROMPT_ANCHOR = '{payload_placeholder}'

// keys of GET /homepage/aiReport/debug/calls
export const AI_REPORT_DEBUG_CALLS_RESPONSE_KEYS = {
  GENERATION: 'generation',
  TOTAL: 'total'
}

export const AI_REPORT_DEBUG_GENERATION_KEYS = {
  STARTED_AT: 'startedAt',
  MODEL: 'model',
  EFFORT: 'effort',
  TIME_LIMIT_SECONDS: 'timeLimitSeconds',
  PAIRS_PER_CALL: 'pairsPerCall',
  ELEMENTS_PER_CALL: 'elementsPerCall',
  PROMPTS_EDITED: 'promptsEdited',
  OUTCOME: 'outcome',
  REASON: 'reason',
  INPUT_TOKENS: 'inputTokens',
  OUTPUT_TOKENS: 'outputTokens',
  COST: 'cost',
  PRICED: 'priced',
  DURATION_MS: 'durationMs',
  CALLS: 'calls'
}

export const AI_REPORT_DEBUG_CALL_KEYS = {
  INDEX: 'index',
  KIND: 'kind',
  MODEL: 'model',
  EFFORT: 'effort',
  SYSTEM_PROMPT: 'systemPrompt',
  USER_PROMPT: 'userPrompt',
  ANSWER: 'answer',
  OUTCOME: 'outcome',
  REASON: 'reason',
  INPUT_TOKENS: 'inputTokens',
  OUTPUT_TOKENS: 'outputTokens',
  COST: 'cost',
  PRICED: 'priced',
  DURATION_MS: 'durationMs'
}

export const AI_REPORT_DEBUG_TOTAL_KEYS = {
  COST: 'cost',
  CURRENCY: 'currency',
  GENERATIONS: 'generations'
}

export const AI_REPORT_DEBUG_CALL_KINDS = {
  BATCH: 'BATCH',
  SUMMARY: 'SUMMARY'
}

export const AI_REPORT_DEBUG_CALL_OUTCOMES = {
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
  TIMEOUT: 'TIMEOUT'
}

export const AI_REPORT_DEBUG_CALL_OUTCOME_LABEL_KEYS = {
  [AI_REPORT_DEBUG_CALL_OUTCOMES.SUCCESS]: 'homepage.ai_reports.debug.call_outcome.success',
  [AI_REPORT_DEBUG_CALL_OUTCOMES.FAILURE]: 'homepage.ai_reports.debug.call_outcome.failure',
  [AI_REPORT_DEBUG_CALL_OUTCOMES.TIMEOUT]: 'homepage.ai_reports.debug.call_outcome.timeout'
}

export const AI_REPORT_DEBUG_GENERATION_OUTCOME_LABEL_KEYS = {
  SUCCEEDED: 'homepage.ai_reports.debug.generation_outcome.succeeded',
  FAILED: 'homepage.ai_reports.debug.generation_outcome.failed',
  TIMED_OUT: 'homepage.ai_reports.debug.generation_outcome.timed_out',
  SELECTION_DELETED: 'homepage.ai_reports.debug.generation_outcome.selection_deleted'
}

// the costs are in US dollars (FR-5.2)
export const AI_REPORT_DEBUG_COST_CURRENCY = 'USD'
